import React, { useMemo, useEffect } from "react";

import { MoreIcon } from "@packages/ds";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";

import SubNavBar from "Components/SubNavBar";
import { NavigationTabType } from "Components/SubNavBar/types";
import config from "console_config";
import * as Dropdown from "ds/Dropdown";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import getUrl from "Libs/getUrl";
import { isDg3 } from "Libs/utils";
import { selectCurrentUserIsInternal } from "Reducers/app/selectors";
import { deploymentSizingApiEnabledSelector } from "Reducers/environment/deployment";
import { selectHasDedicatedDeployment } from "Reducers/environment/environment.selectors";
import { useCurrentService } from "Reducers/environment/hooks/useCurrentService";
import { useCurrentWorker } from "Reducers/environment/hooks/useCurrentWorker";
import {
  selectFeatureServiceTreeEnabled,
  selectFeatureProfilingEnabled,
  selectFeatureUsageBasedBillingEnabled
} from "Reducers/featureFlags/featureFlags.selectors";
import { selectSubscriptionDetails } from "Reducers/organization/subscription";
import { gitProjectSelector } from "Reducers/project/project";
import { selectAdminAgentUrl } from "Reducers/serviceProcesses/serviceProcesses.selectors";
import { useAutoscalingConfigLink } from "src/organization/pages/project/environment/ConfigureAutoscaling/useAutoscalingConfigLink";
import { useAutoscallingValues } from "src/organization/pages/project/environment/metrics/containers/Service/util";
import useMetricsLinkData from "src/organization/pages/project/environment/metrics/hooks/useMetricsLinkData";
import { useAppSelector } from "Store/hooks";

import * as S from "./ServiceInfoPanel.styles";
import ServiceInfoPanelTitle from "./ServiceInfoPanelTitle";

interface CurrentServiceDecodedParams {
  environmentId: string;
  organizationId: string;
  projectId: string;
  appName: string;
}

export interface ServiceInfoPanelProps {
  children: React.ReactNode;
}

const ServiceInfoPanel = ({ children }: ServiceInfoPanelProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentService = useCurrentService();
  const currentWorker = useCurrentWorker();
  const intl = useIntl();

  const {
    environmentId,
    organizationId,
    projectId,
    appName = "routes"
  } = useDecodedParams<CurrentServiceDecodedParams>();

  const isProfilingSupported = config.CUSTOM_PROFILING_LANGUAGE?.some?.(
    lang => currentService?.type?.toLowerCase().indexOf(lang) != -1
  );
  const { hasCapability } = useAutoscallingValues(currentService?.name);

  useEffect(() => {
    if (
      (currentService?.class !== "app" &&
        !currentWorker &&
        location.pathname.includes("/profiling")) ||
      (!isProfilingSupported && location.pathname.includes("/profiling"))
    ) {
      navigate(
        `/${organizationId}/${projectId}/${encodeURIComponent(
          environmentId
        )}/services/${appName}`
      );
    }
  }, [
    appName,
    location.pathname,
    organizationId,
    projectId,
    environmentId,
    navigate,
    currentService?.class,
    isProfilingSupported,
    currentWorker
  ]);

  const adminAgentUrl = useSelectorWithUrlParams(selectAdminAgentUrl);
  const isInternalUser = useAppSelector(selectCurrentUserIsInternal);

  const hasAdminAgentUrl = Boolean(adminAgentUrl);

  const { autoscalingEnabledFlag, handleConfigureAutoscaling } =
    useAutoscalingConfigLink(currentService);

  const isSizingApiEnabled = useAppSelector(state =>
    deploymentSizingApiEnabledSelector(state, {
      organizationId,
      projectId,
      environmentId
    })
  );

  const name = currentWorker?.name || currentService?.name || "router";
  const project = useSelectorWithUrlParams(gitProjectSelector);
  const dedicatedDeployment = useSelectorWithUrlParams(
    selectHasDedicatedDeployment
  );

  const subscription = useAppSelector(state =>
    selectSubscriptionDetails(state, {
      organizationId,
      subscriptionId: project?.getSubscriptionId()
    })
  );

  const serviceTreeEnabled = useAppSelector(selectFeatureServiceTreeEnabled);

  const isDg3Project = isDg3(subscription?.enterprise_tag, dedicatedDeployment);

  const serviceTreeOrDg3 = serviceTreeEnabled || isDg3Project;

  const chorusLink = useMetricsLinkData().href;

  const profilingEnabled = useAppSelector(selectFeatureProfilingEnabled);
  const isUsageBilling = useAppSelector(selectFeatureUsageBasedBillingEnabled);

  const { support_tier } = subscription || {};

  const isEnterpriseTier = !["trial", "standard"].includes(support_tier ?? "");
  const enableProfilingTab = !!(isUsageBilling
    ? profilingEnabled
    : profilingEnabled && isEnterpriseTier && isInternalUser);

  const tabs = useMemo(() => {
    const type = currentWorker ? "worker" : currentService?.class || appName;
    const tabs: NavigationTabType[] = [
      {
        name: "overview",
        route: `/${organizationId}/${projectId}/${encodeURIComponent(
          environmentId
        )}/services/${appName}`
      },
      {
        name: "configuration",
        route: `/${organizationId}/${projectId}/${encodeURIComponent(
          environmentId
        )}/services/${appName}/configuration`
      }
    ];

    if (currentService?.class === "app" || currentWorker) {
      tabs.push({
        name: "profiling",
        tooltip: !isProfilingSupported ? "Profiling not supported" : undefined,
        route: `/${organizationId}/${projectId}/${encodeURIComponent(
          environmentId
        )}/services/${appName}/profiling`,
        disabled: !isProfilingSupported,
        permission: () => enableProfilingTab
      });
    }
    if (hasAdminAgentUrl && isInternalUser) {
      tabs.push({
        name: "processes",
        route: `/${organizationId}/${projectId}/${encodeURIComponent(
          environmentId
        )}/services/${appName}/processes`
      });
    }

    return tabs
      .filter(() => ["app", "routes", "service", "worker"].includes(type))
      .filter(value =>
        !serviceTreeOrDg3
          ? value.name === "configuration"
            ? null
            : value
          : value
      );
  }, [
    appName,
    currentService?.class,
    environmentId,
    organizationId,
    projectId,
    serviceTreeOrDg3,
    hasAdminAgentUrl,
    isInternalUser,
    isProfilingSupported,
    enableProfilingTab,
    currentWorker
  ]);

  const isWorkerOrService = currentService || currentWorker;

  return !isWorkerOrService ? null : (
    <S.Panel>
      <ServiceInfoPanelTitle
        currentService={currentService}
        currentWorker={currentWorker}
        name={name}
        appName={appName}
      />
      {tabs && (
        <S.TabRowWrapper>
          <SubNavBar
            tabs={tabs}
            currentPathName={location.pathname}
            islightbackground
          />
          {(chorusLink || isSizingApiEnabled) && (
            <Dropdown.Root id="service_info_panel">
              <Dropdown.Trigger aria-label="dropdown-metrics">
                <MoreIcon />
              </Dropdown.Trigger>
              <Dropdown.Portal>
                <Dropdown.Content align="end">
                  {currentService?.class === "app" &&
                    hasCapability &&
                    autoscalingEnabledFlag && (
                      <Dropdown.Item onClick={handleConfigureAutoscaling}>
                        {intl.formatMessage({
                          id: "configure.thresholds"
                        })}
                      </Dropdown.Item>
                    )}
                  {chorusLink && (
                    <Dropdown.LinkItem
                      to={`../metrics?range=15m&service=${name}`}
                    >
                      <FormattedMessage id="view_metrics" />
                    </Dropdown.LinkItem>
                  )}
                  {isSizingApiEnabled && (
                    <Dropdown.LinkItem
                      to={getUrl({
                        key: "organization.project.environment.configureResources",
                        props: {
                          organizationId,
                          projectId,
                          environmentId: environmentId || "",
                          location: location.pathname + location.search,
                          queryParams: ""
                        }
                      })}
                    >
                      <FormattedMessage id="configure_resources" />
                    </Dropdown.LinkItem>
                  )}
                </Dropdown.Content>
              </Dropdown.Portal>
            </Dropdown.Root>
          )}
        </S.TabRowWrapper>
      )}
      {children}
    </S.Panel>
  );
};

export default ServiceInfoPanel;
